import React, { useEffect, useState } from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';
import {
  Control,
  Controller,
  FieldError,
  FieldErrors,
  FieldErrorsImpl,
  Merge,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormWatch
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { getCategoryList } from '../../api/db/category';
import { getMaterialList } from '../../api/db/material';
import { getCompanyList, getOperatorUserList } from '../../api/db/user';
import {
  black,
  gray100,
  gray90,
  primary10,
  primaryButtonBackgroundColor,
  primaryColor
} from '../../constants/Colors';
import { userRoleEnum, userStatus, userType } from '../../constants/Enum';
import {
  co2CalcBasisOptions,
  recommendFlgOptions,
  carbonOffsetOptions
} from '../../constants/SelectOptions';
import {
  AdminProductRegisterType,
  DocumentDataType,
  ImageDataType,
  PriceHistoryType
} from '../../validation/products/productType';
import { UserCompanyListType } from '../../validation/user/userType';

import CustomTextfield from './CustomTextfield';
import CustomFileUploader from './CustomFileUploader';
import CustomSelectField, { ListType } from './CustomSelectField';
import CustomRadioButton from './CustomRadioButton';
import CustomDatePicker from './CustomDatePicker';
import { AddErrorList } from '../pages/common/ProductRegister';
import CustomSnackbar from './CustomSnackbar';
import { getMemberListByCompanyId } from '../../api/db/member';

const styles = {
  heading: {
    '@media screen and (max-width:600px)': {
      height: '32px',
      fontSize: '16px'
    },
    display: 'flex',
    alignItems: 'center',
    backgroundColor: primary10,
    height: '42px',
    pl: '15px',
    fontSize: '18px',
    fontWeight: 500,
    color: gray100,
    borderLeft: `3px solid ${primaryColor}`,
    mb: '24px'
  },
  co2CalcForm: {
    borderLeft: `3px solid ${primaryColor}`,
    pl: '16px',
    mb: '24px'
  },
  co2CalcText: {
    fontSize: '14px',
    color: primaryColor
  },
  unitText: {
    fontSize: '16px',
    ml: '8px',
    color: gray100
  },
  button: {
    '@media screen and (max-width:1440px)': {
      display: 'none'
    },
    width: '100%',
    fontSize: '16px',
    fontWeight: 500,
    mb: '25px',
    ...primaryButtonBackgroundColor
  },
  deleteButton: {
    '@media screen and (max-width:1440px)': {
      display: 'none'
    },
    color: primaryColor,
    fontSize: '16px',
    fontWeight: 500,
    width: '100%',
    textDecorationLine: 'underline',
    '&:hover': {
      textDecorationLine: 'underline'
    }
  },
  deleteModal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50%',
    maxWidth: '512px',
    minWidth: '294px',
    bgcolor: 'white',
    p: '24px'
  }
};

type Props = {
  control: Control<AdminProductRegisterType>;
  setValue: UseFormSetValue<AdminProductRegisterType>;
  errors: FieldErrors<AdminProductRegisterType>;
  handleSubmit: UseFormHandleSubmit<AdminProductRegisterType>;
  watch: UseFormWatch<AdminProductRegisterType>;
  isButtonDisabled: boolean;
  isNew: boolean;
  draftFlg?: boolean;
  onClickDeleteButton?: () => void;
  onSubmit: () => Promise<void>;
  getCo2Unit?: string;
  getCostUnit?: string;
  addErrorList?: AddErrorList | null;
  co2RequiredCheck: () => boolean;
  caluclatedNameCheck: () => boolean;
  setFirstSubmit?: React.Dispatch<React.SetStateAction<boolean>>;
  priceHistory?: PriceHistoryType[];
};

interface CategoryType {
  id: number;
  name: string;
}

interface MaterialType {
  id: number;
  name: string;
  category_id: number;
  category_name: string;
  calculation_specification: string | null;
  price_condition: string | null;
  co2_unit: string;
  cost_unit: string;
}

const AdminProductRegisterForm = (props: Props) => {
  const {
    errors,
    control,
    handleSubmit,
    onSubmit,
    isButtonDisabled,
    setValue,
    watch,
    isNew,
    draftFlg,
    onClickDeleteButton,
    getCo2Unit,
    getCostUnit,
    addErrorList,
    co2RequiredCheck,
    caluclatedNameCheck,
    setFirstSubmit,
    priceHistory
  } = props;

  // マスタの取得
  const [materialList, setMaterialList] = useState<MaterialType[]>([]);

  // プルダウン選択用整形
  const [categoryOptions, setCategoryOptions] = useState<ListType[]>([]);
  const [materialOptions, setMaterialOptions] = useState<ListType[]>([]);
  const [assigneeOptions, setAssigneeOptions] = useState<ListType[]>([]);

  // 部材名 プルダウンのdisabled、height
  const [materialDisabled, setMaterialDisabled] = useState<boolean>(true);
  const [materialHeight, setMaterialHeight] = useState<string>('40px');

  // 画面のアクセス制御
  const navigate = useNavigate();
  const currentUserType = Number(localStorage.getItem('user_type'));

  useEffect(() => {
    void (async () => {
      // 大分類マスタの取得
      const categoryRes = await getCategoryList();
      const categories = categoryRes.data.map((item: CategoryType) => {
        return {
          value: item.id,
          option: item.name,
          disabled: false
        };
      });
      setCategoryOptions(categories);

      // 部材名マスタの取得
      const materialRes = await getMaterialList();
      setMaterialList(materialRes.data);
    })();
  }, []);

  const [co2Unit, setCo2Unit] = useState<string>('');
  const [costUnit, setCostUnit] = useState<string>('');

  useEffect(() => {
    void (async () => {
      // 部材名 選択肢の絞り込み
      if (!watch('categoryId') || !materialList) return;
      const materials = materialList
        .filter((item: { category_id: number }) => {
          return item.category_id === watch('categoryId');
        })
        .map((item: MaterialType) => {
          return {
            value: item.id,
            option: item.name,
            disabled: false
          };
        });
      const currentMaterial = materials.find(
        (item: { value: number }) => item.value === watch('materialId')
      );
      materials.length > 0 && !currentMaterial && setValue('materialId', 0);
      setMaterialOptions(materials);
      setMaterialDisabled(false);
    })();
  }, [materialList, watch('categoryId')]);

  // 部材名 選択後のCO2算定仕様の取得
  useEffect(() => {
    if (!watch('materialId')) return;
    const material = materialList.find((item: MaterialType) => {
      return item.id === watch('materialId');
    });
    if (!material) return;
    setValue('calculationSpecification', material.calculation_specification);
    setValue('priceCondition', material.price_condition);
    setCo2Unit(material.co2_unit);
    setCostUnit(material.cost_unit);

    // 部材名が長い場合、inputの高さを調整
    const selectedMaterial = materialOptions.find(
      (option) => option?.value === watch('materialId')
    );
    if (!selectedMaterial) return;
    const isTooLong = selectedMaterial.option?.length > 20;
    if (isTooLong) setMaterialHeight('100%');
  }, [watch('materialId')]);

  // 単位のない部材名を選択した場合、参考設計単価をdisabled/nullにする
  useEffect(() => {
    if (costUnit === '―') {
      setValue('referenceUnitPrice', null);
      setValue('settingAt', null);
    }
  }, [costUnit]);

  // 単位まわり編集時の初期値設定
  useEffect(() => {
    getCostUnit && setCostUnit(getCostUnit);
    getCo2Unit && setCo2Unit(getCo2Unit);
  }, [getCostUnit, getCo2Unit]);

  // 企業一覧と事務局担当者一覧の取得
  const [comapnyList, setCompanyList] = useState<ListType[]>([]);
  const [operatorList, setOperatorList] = useState<ListType[]>([]);

  useEffect(() => {
    void (async () => {
      // 事務局画面で事務局ユーザーではない場合は、ホームに飛ばす
      if (currentUserType !== userType.admin) return navigate('/home');

      const res = await getCompanyList();
      const data = res.data.companies;
      const sortedUserData = data.sort(
        (a: UserCompanyListType, b: UserCompanyListType) =>
          a.company_id - b.company_id
      );
      const list = sortedUserData.map((item: UserCompanyListType) => {
        return {
          option: `${item.company_id}: ${item.company_short}`,
          value: item.company_id,
          disabled: false
        };
      });
      setCompanyList(list);

      // 事務局担当者一覧の取得
      const resOperatorUser = await getOperatorUserList();
      const operatorUserData = resOperatorUser.data.users;
      const operatorUserlist = operatorUserData.map(
        (item: UserCompanyListType) => {
          return {
            option: `${item.last_name} ${item.first_name}`,
            value: `${item.last_name} ${item.first_name}`
          };
        }
      );
      setOperatorList(operatorUserlist);
    })();
  }, []);

  const [leaderId, setLeaderId] = useState<string | null>(null);
  const [firstCompanySetting, setFirstCompanySetting] = useState<boolean>(true);
  const [firstAssigneeSetting, setFirstAssigneeSetting] =
    useState<boolean>(true);

  // 企業が選択されたら、企業を指定して担当者一覧を取得
  useEffect(() => {
    void (async () => {
      if (watch('company')) {
        // 企業を指定して担当者一覧の取得
        const memberRes = await getMemberListByCompanyId(watch('company'));
        const members = Object.values(memberRes.data.members);
        const formattedMembers = members
          .filter((item) => item.status === userStatus.available)
          .map((item) => {
            return {
              value: item.user_id,
              option: `${item.last_name} ${item.first_name}`,
              disabled: false
            };
          });
        setAssigneeOptions(formattedMembers);

        // 代表者のIDを取得
        const leaderId = members.find(
          (item) => item.role === userRoleEnum.leader
        );
        leaderId && setLeaderId(leaderId?.user_id.toString());
      }
    })();
  }, [watch('company')]);

  // 担当者一覧が取得できたら、担当者のデフォルト値を企業の代表者に設定
  useEffect(() => {
    // 編集時は初期値が設定されているので、代表者を設定しない
    if (firstCompanySetting && !isNew) {
      setFirstCompanySetting(false);
      return;
    }
    if (firstAssigneeSetting && !isNew) {
      setFirstAssigneeSetting(false);
      return;
    }
    // 編集の初期値以外は、代表者を設定
    if (assigneeOptions && leaderId) {
      setValue('assignee', String(leaderId));
    }
  }, [assigneeOptions]);

  const getImagesErrorMessage = (
    errors:
      | Merge<
          FieldError,
          (
            | Merge<
                FieldError,
                FieldErrorsImpl<{
                  id: number;
                  sort: number;
                  binary: string;
                  kind: string;
                  name: string;
                  size: number;
                }>
              >
            | undefined
          )[]
        >
      | undefined
  ) => {
    let errorMessage = '';

    if (errors) {
      if (errors[0] && errors[0].name && errors[0].name.message) {
        errorMessage = errors[0].name.message;
        return errorMessage;
      } else if (errors[1] && errors[1].name && errors[1].name.message) {
        errorMessage = errors[1].name.message;
        return errorMessage;
      } else if (errors[2] && errors[2].name && errors[2].name.message) {
        errorMessage = errors[2].name.message;
        return errorMessage;
      }
      if (errors[0] && errors[0].size && errors[0].size.message) {
        errorMessage = errors[0].size.message;
        return errorMessage;
      } else if (errors[1] && errors[1].size && errors[1].size.message) {
        errorMessage = errors[1].size.message;
        return errorMessage;
      } else if (errors[2] && errors[2].size && errors[2].size.message) {
        errorMessage = errors[2].size.message;
        return errorMessage;
      }
    }

    return errorMessage;
  };

  const getDocumentErrorMessage = (
    errors:
      | Merge<
          FieldError,
          FieldErrorsImpl<{
            name: string;
            binary: string;
            kind: string;
            id: number;
            size: number;
          }>
        >
      | undefined
  ) => {
    let errorMessage = '';

    if (errors) {
      if (errors && errors.name && errors.name.message) {
        errorMessage = errors.name.message;
        return errorMessage;
      }
      if (errors && errors.size && errors.size.message) {
        errorMessage = errors.size.message;
        return errorMessage;
      }
    }

    return errorMessage;
  };

  // 参考設計単価のインデックス
  const circleNumbers = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'];

  // SbackBarsの表示テキスト
  const textSnackbars = () => {
    const list = [];
    if (errors.company) {
      list.push(`「企業名」は${errors.company.message}`);
    }
    if (addErrorList?.companyRequiredError?.error) {
      list.push(
        `「企業名」は${addErrorList.companyRequiredError.errorMessage}`
      );
    }
    if (errors.alias) {
      list.push(`「一般名称」は${errors.alias.message}`);
    }
    if (errors.name) {
      list.push(`「商品名」は${errors.name.message}`);
    }
    if (addErrorList?.nameRequiredError?.error) {
      list.push(`「商品名」は${addErrorList.nameRequiredError.errorMessage}`);
    }
    if (errors.specification) {
      list.push(`「仕様」は${errors.specification.message}`);
    }
    if (errors.summary) {
      list.push(`「概要説明」は${errors.summary.message}`);
    }
    if (errors.condition) {
      list.push(`「適用条件」は${errors.condition.message}`);
    }
    if (errors.images) {
      list.push(`「説明画像」は${getImagesErrorMessage(errors.images)}`);
    }
    if (errors.documentDescriptionUrl) {
      list.push(`「補足説明用 URL」は${errors.documentDescriptionUrl.message}`);
    }
    if (errors.document) {
      list.push(
        `「補足説明資料」は${getDocumentErrorMessage(errors.document)}`
      );
    }
    if (errors.categoryId) {
      list.push(`「大分類」は${errors.categoryId.message}`);
    }
    if (errors.materialId) {
      list.push(`「部材名」は${errors.materialId.message}`);
    }
    if (addErrorList?.co2RequiredError?.error) {
      list.push(addErrorList.co2RequiredError.errorMessage);
    }
    if (errors.emissionIntensity) {
      list.push(
        `「算定したCO2排出原単位」は${errors.emissionIntensity.message}`
      );
    }
    if (errors.reduceRate) {
      list.push(`「CO2削減率」は${errors.reduceRate.message}`);
    }
    if (errors.calculationSupplement) {
      list.push(`「CO2算定の補足」は${errors.calculationSupplement.message}`);
    }
    if (errors.calculationBasisType) {
      list.push(`「CO2算定根拠 種別」は${errors.calculationBasisType.message}`);
    }
    if (errors.calculationBasisName) {
      list.push(`「CO2算定根拠 名称」は${errors.calculationBasisName.message}`);
    }
    if (addErrorList?.co2BasisNameRequiredError?.error) {
      list.push(
        `「CO2算定根拠 名称」は${addErrorList?.co2BasisNameRequiredError?.errorMessage}`
      );
    }
    if (errors.calculationBasisFile) {
      list.push(`CO2算定根拠 ファイルは${errors.calculationBasisFile.message}`);
    }
    if (errors.calculationBasisFileUrl) {
      list.push(`CO2算定根拠 URLは${errors.calculationBasisFileUrl.message}`);
    }
    if (errors.carbonOffsetFlg) {
      list.push(
        `「カーボンオフセットの利用」は${errors.carbonOffsetFlg.message}`
      );
    }
    if (errors.otherCertification) {
      list.push(`「その他の認定情報」は${errors.otherCertification.message}`);
    }
    if (errors.priceCondition) {
      list.push(`「単価算定条件」は${errors.priceCondition.message}`);
    }
    if (errors.referenceUnitPrice) {
      list.push(`「参考設計単価」は${errors.referenceUnitPrice.message}`);
    }
    if (errors.settingAt) {
      list.push(`「単価設定日」は${errors.settingAt.message}`);
    }
    if (errors.priceRemarks) {
      list.push(`「単価補足説明」は${errors.priceRemarks.message}`);
    }
    if (errors.relatedUrl) {
      list.push(`「関連URL」は${errors.relatedUrl.message}`);
    }
    if (errors.remark) {
      list.push(`「その他備考」は${errors.remark.message}`);
    }
    if (errors.assignee) {
      list.push(`「担当者」は${errors.assignee.message}`);
    }
    if (errors.recommendFlg) {
      list.push(`「事務局おすすめ」は${errors.recommendFlg.message}`);
    }
    if (errors.operatorName) {
      list.push(`「事務局おすすめ」は${errors.operatorName.message}`);
    }
    if (errors.operatorMemo) {
      list.push(`「事務局おすすめ」は${errors.operatorMemo.message}`);
    }
    return list.join('\n');
  };

  return (
    <Grid>
      <Box sx={styles.heading}>一般情報</Box>
      <Controller
        name="company"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <CustomSelectField
            {...field}
            value={field.value}
            onChange={field.onChange}
            required
            requiredText="必須"
            labelText="企業名"
            formMb="24px"
            list={comapnyList}
            error={
              Boolean(errors.company) ||
              addErrorList?.companyRequiredError?.error
            }
            helperText={
              errors.company?.message ??
              addErrorList?.companyRequiredError?.errorMessage ??
              ''
            }
          />
        )}
      />
      <Controller
        name="alias"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            error={Boolean(errors.alias)}
            helperText={errors.alias?.message}
            labelText="一般名称"
            requiredText="必須"
            placeholder="低炭素型コンクリート"
            formMb="24px"
            width="100%"
          />
        )}
      />
      <Controller
        name="name"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            error={
              Boolean(errors.name) || addErrorList?.nameRequiredError?.error
            }
            helperText={
              errors.name?.message ??
              addErrorList?.nameRequiredError?.errorMessage ??
              ''
            }
            labelText="商品名"
            requiredText="必須"
            placeholder="〇〇〇コンクリート"
            formMb="24px"
            width="100%"
          />
        )}
      />
      <Controller
        name="specification"
        control={control}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            error={Boolean(errors.specification)}
            helperText={errors.specification?.message}
            labelText="仕様"
            placeholder="01タイプ"
            formMb="24px"
            width="100%"
          />
        )}
      />
      <Controller
        name="summary"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            error={Boolean(errors.summary)}
            helperText={errors.summary?.message}
            labelText="概要説明"
            requiredText="必須"
            placeholder="〇〇コンクリートは、□□□することによって、通常のコンクリートと比べてCO2排出量を約20％削減できます。"
            formMb="24px"
            multiline
            fieldType="textareaRow8"
            rows={8}
            width="100%"
          />
        )}
      />
      <Controller
        name="condition"
        control={control}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            error={Boolean(errors.condition)}
            helperText={errors.condition?.message}
            labelText="適用条件"
            placeholder="地下構造物のみ"
            formMb="24px"
            width="100%"
          />
        )}
      />
      <Controller
        name="images"
        control={control}
        render={({ field }) => (
          <CustomFileUploader
            labelText="説明画像"
            supplementaryLabelText="(3枚までアップロードできます)"
            formMb="24px"
            value={field.value as ImageDataType[]}
            onChange={field.onChange}
            error={Boolean(errors.images)}
            helperText={getImagesErrorMessage(errors.images)}
            fileType="images"
          />
        )}
      />
      <Controller
        name="document"
        control={control}
        render={({ field }) => (
          <CustomFileUploader
            labelText="補足説明資料"
            formMb="24px"
            value={field.value as DocumentDataType}
            onChange={field.onChange}
            fileType="document"
            error={Boolean(errors.document)}
            helperText={getDocumentErrorMessage(errors.document)}
          />
        )}
      />
      <Controller
        name="documentDescriptionUrl"
        control={control}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            labelText="補足説明用 URL"
            placeholder="https://***"
            formMb="24px"
            error={Boolean(errors.documentDescriptionUrl)}
            helperText={errors.documentDescriptionUrl?.message}
            width="100%"
          />
        )}
      />
      <Controller
        name="categoryId"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <CustomSelectField
            {...field}
            value={field.value}
            onChange={field.onChange}
            required
            requiredText="必須"
            labelText="大分類"
            formMb="24px"
            list={categoryOptions}
            error={Boolean(errors.categoryId)}
            helperText={errors.categoryId?.message}
          />
        )}
      />
      <Controller
        name="materialId"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <CustomSelectField
            {...field}
            value={field.value}
            onChange={field.onChange}
            required
            requiredText="必須"
            labelText="部材名"
            formMb="48px"
            list={materialOptions}
            error={Boolean(errors.materialId)}
            height={materialHeight}
            helperText={errors.materialId?.message}
            placeholder="先に大分類を選択してください"
            disabled={materialDisabled}
          />
        )}
      />
      <Box sx={styles.heading}>CO2に関する情報</Box>
      <Controller
        name="calculationSpecification"
        control={control}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            labelText="基準CO2算定仕様"
            formMb="24px"
            disabled
            error={Boolean(errors.calculationSpecification)}
            helperText={errors.calculationSpecification?.message}
          />
        )}
      />
      <Grid container sx={styles.co2CalcForm}>
        <Grid container flexDirection={'column'} sx={{ mb: '16px' }}>
          <Typography sx={styles.co2CalcText}>
            ・算定したCO2排出原単位かCO2削減率のどちらか一方は入力してください
          </Typography>
          <Typography sx={styles.co2CalcText}>
            ・両方入力いただくと登録する建材が検索されやすくなります
          </Typography>
          <Typography sx={styles.co2CalcText}>
            ・算定したCO2排出原単位は、100以上は整数、100未満は小数第3位までで入力してください
          </Typography>
          <Typography sx={styles.co2CalcText}>
            ・CO2削減率は0〜100の範囲で、小数第1位までで入力してください
          </Typography>
        </Grid>
        <Grid container alignItems="flex-end" mb="24px">
          <Controller
            name="emissionIntensity"
            control={control}
            render={({ field }) => (
              <CustomTextfield
                {...field}
                value={field.value}
                onChange={field.onChange}
                labelText="算定したCO2排出原単位"
                placeholder="240"
                formMb="0px"
                width="248px"
                error={
                  Boolean(errors.emissionIntensity) ||
                  addErrorList?.co2RequiredError?.error
                }
                helperText={
                  errors.emissionIntensity?.message ??
                  addErrorList?.co2RequiredError?.errorMessage ??
                  ''
                }
              />
            )}
          />
          <Typography sx={styles.unitText}>kg-CO2/{co2Unit}</Typography>
        </Grid>
        <Typography sx={{ color: black, fontSize: '14px' }}>
          CO2削減率
        </Typography>
        <Grid container alignItems="flex-end">
          <Typography
            sx={{
              fontSize: '16px',
              mr: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'center'
            }}
          >
            △
          </Typography>
          <Controller
            name="reduceRate"
            control={control}
            render={({ field }) => (
              <CustomTextfield
                {...field}
                value={field.value}
                onChange={field.onChange}
                placeholder="20.0"
                formMb="0px"
                width="224px"
                error={
                  Boolean(errors.reduceRate) ||
                  addErrorList?.co2RequiredError?.error
                }
                helperText={
                  errors.reduceRate?.message ??
                  addErrorList?.co2RequiredError?.errorMessage ??
                  ''
                }
              />
            )}
          />
          <Typography sx={styles.unitText}>%</Typography>
        </Grid>
      </Grid>
      <Controller
        name="calculationSupplement"
        control={control}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            labelText="CO2算定の補足"
            formMb="24px"
            multiline
            rows={3}
            fieldType="textareaRow3"
            placeholder="通常のコンクリートを〇〇データベースから300kg-CO2/m3として設定し、CO2削減量が△60kg/m2のため、60/300=20％削減とした。"
            error={Boolean(errors.calculationSupplement)}
            helperText={errors.calculationSupplement?.message}
            width="100%"
          />
        )}
      />
      <Controller
        name="calculationBasisType"
        control={control}
        render={({ field }) => (
          <CustomRadioButton
            {...field}
            value={field.value}
            onChange={field.onChange}
            options={co2CalcBasisOptions}
            labelText="CO2算定根拠 種別"
            requiredText="必須"
            formMb="24px"
            error={Boolean(errors.calculationBasisType)}
            helperText={errors.calculationBasisType?.message}
          />
        )}
      />
      <Controller
        name="calculationBasisName"
        control={control}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            labelText="CO2算定根拠 名称"
            placeholder="〇〇〇〇"
            formMb="24px"
            error={
              Boolean(errors.calculationBasisName) ||
              addErrorList?.co2BasisNameRequiredError?.error
            }
            helperText={
              errors.calculationBasisName?.message ??
              addErrorList?.co2BasisNameRequiredError?.errorMessage ??
              ''
            }
            required={
              Number(watch('calculationBasisType')) ===
              co2CalcBasisOptions[0].value
            }
            requiredText={
              Number(watch('calculationBasisType')) ===
              co2CalcBasisOptions[0].value
                ? '必須'
                : ''
            }
            width="100%"
          />
        )}
      />
      <Controller
        name="calculationBasisFile"
        control={control}
        render={({ field }) => (
          <CustomFileUploader
            labelText="CO2算定根拠 ファイル"
            formMb="24px"
            value={field.value as DocumentDataType}
            onChange={field.onChange}
            fileType="document"
            error={Boolean(errors.calculationBasisFile)}
            helperText={getDocumentErrorMessage(errors.calculationBasisFile)}
          />
        )}
      />
      <Controller
        name="calculationBasisFileUrl"
        control={control}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            labelText="CO2算定根拠 URL"
            placeholder="https://***"
            formMb="24px"
            error={Boolean(errors.calculationBasisFileUrl)}
            helperText={errors.calculationBasisFileUrl?.message}
            width="100%"
          />
        )}
      />
      <Controller
        name="carbonOffsetFlg"
        control={control}
        render={({ field }) => (
          <CustomRadioButton
            {...field}
            value={field.value}
            onChange={field.onChange}
            options={carbonOffsetOptions}
            labelText="カーボンオフセットの利用"
            requiredText="必須"
            formMb="24px"
            error={Boolean(errors.carbonOffsetFlg)}
            helperText={errors.carbonOffsetFlg?.message}
          />
        )}
      />
      <Controller
        name="otherCertification"
        control={control}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            labelText="その他の認定情報"
            formMb="48px"
            multiline
            rows={4}
            fieldType="textareaRow4"
            error={Boolean(errors.otherCertification)}
            helperText={errors.otherCertification?.message}
            width="100%"
          />
        )}
      />
      <Box sx={styles.heading}>コスト情報</Box>
      <Controller
        name="priceCondition"
        control={control}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            labelText="単価算定条件"
            formMb="24px"
            disabled
            error={Boolean(errors.priceCondition)}
            helperText={errors.priceCondition?.message}
          />
        )}
      />
      <Grid
        container
        alignItems={costUnit === '―' ? 'center' : 'flex-end'}
        mb="24px"
      >
        <Controller
          name="referenceUnitPrice"
          control={control}
          render={({ field }) => (
            <CustomTextfield
              {...field}
              value={field.value}
              onChange={field.onChange}
              labelText="参考設計単価"
              placeholder={costUnit === '―' ? '' : '10000'}
              formMb="0px"
              width="248px"
              error={Boolean(errors.referenceUnitPrice)}
              helperText={errors.referenceUnitPrice?.message}
              disabled={costUnit === '―' ? true : false}
              attentionText={
                costUnit === '―' ? '条件設定が困難なため単価は記載なし' : ''
              }
            />
          )}
        />
        <Typography sx={styles.unitText}>円/{costUnit}</Typography>
      </Grid>
      <Controller
        render={({ field }) => (
          <CustomDatePicker
            {...field}
            labelText="単価設定日"
            placeholder={costUnit === '―' ? '' : '2024/01/01'}
            disabled={costUnit === '―' ? true : false}
            disabledColor={black}
            width="248px"
            formMb={!isNew && !draftFlg ? '4px' : '24px'}
            isRequired={false}
            setValue={(newValue) => {
              setValue('settingAt', newValue);
            }}
            error={Boolean(errors.settingAt)}
            helperText={errors.settingAt?.message}
          />
        )}
        control={control}
        name="settingAt"
      />
      {!isNew && !draftFlg && (
        <>
          {priceHistory && priceHistory.length > 0 ? (
            <Typography sx={{ color: gray90, fontSize: '12px', mb: '2px' }}>
              過去の修正日・単価
            </Typography>
          ) : (
            <></>
          )}
          <Box sx={{ mb: '32px' }}>
            {priceHistory?.map((price: PriceHistoryType, index: number) => {
              return (
                <Typography
                  component={'span'}
                  sx={{ color: gray90, fontSize: '12px', mr: '16px' }}
                >
                  {circleNumbers[index]} {price.settingAt}{' '}
                  {price.referenceUnitPrice}円/
                  {price.costUnit}
                </Typography>
              );
            })}
          </Box>
        </>
      )}
      <Controller
        name="priceRemarks"
        control={control}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            labelText="単価補足説明"
            placeholder="単価についての補足(例:コスト１割アップ程度)"
            multiline
            rows={4}
            fieldType="textareaRow4"
            formMb="40px"
            error={Boolean(errors.priceRemarks)}
            helperText={errors.priceRemarks?.message}
            width="100%"
          />
        )}
      />
      <Box sx={styles.heading}>その他</Box>
      <Controller
        name="relatedUrl"
        control={control}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            labelText="関連URL"
            placeholder=""
            formMb="24px"
            error={Boolean(errors.relatedUrl)}
            helperText={errors.relatedUrl?.message}
            width="100%"
          />
        )}
      />
      <Controller
        name="remark"
        control={control}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            labelText="その他備考"
            multiline
            rows={4}
            fieldType="textareaRow4"
            formMb="40px"
            error={Boolean(errors.remark)}
            helperText={errors.remark?.message}
            width="100%"
          />
        )}
      />
      <Controller
        name="assignee"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <CustomSelectField
            {...field}
            value={field.value}
            onChange={field.onChange}
            required
            requiredText="必須"
            labelText="担当者"
            formMb="24px"
            list={assigneeOptions}
            error={Boolean(errors.assignee)}
            helperText={errors.assignee?.message}
          />
        )}
      />
      <Box sx={styles.heading}>事務局</Box>
      <Controller
        name="recommendFlg"
        control={control}
        render={({ field }) => (
          <CustomRadioButton
            {...field}
            value={field.value}
            onChange={field.onChange}
            options={recommendFlgOptions}
            labelText="事務局おすすめ"
            requiredText="必須"
            formMb="24px"
            error={Boolean(errors.recommendFlg)}
            helperText={errors.recommendFlg?.message}
          />
        )}
      />
      <Controller
        name="operatorName"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <CustomSelectField
            {...field}
            value={field.value}
            onChange={field.onChange}
            labelText="事務局担当者"
            formMb="24px"
            list={operatorList}
            placeholder="選択してください"
            error={Boolean(errors.operatorName)}
            helperText={errors.operatorName?.message}
          />
        )}
      />
      <Controller
        name="operatorMemo"
        control={control}
        render={({ field }) => (
          <CustomTextfield
            {...field}
            value={field.value}
            onChange={field.onChange}
            labelText="事務局メモ"
            multiline
            rows={4}
            fieldType="textareaRow4"
            formMb="40px"
            error={Boolean(errors.operatorMemo)}
            helperText={errors.operatorMemo?.message}
            width="100%"
          />
        )}
      />
      <Button
        variant="contained"
        sx={styles.button}
        onClick={async () => {
          setFirstSubmit && setFirstSubmit(true);
          const co2RequiredFlg = co2RequiredCheck();
          const caluclatedNameFlg = caluclatedNameCheck();
          await handleSubmit(async () => {
            !co2RequiredFlg && !caluclatedNameFlg && (await onSubmit());
          })();
        }}
        disabled={isButtonDisabled}
      >
        {isNew || draftFlg ? '建材を登録する' : '建材を更新する'}
      </Button>
      {!isNew && (
        <Button sx={styles.deleteButton} onClick={onClickDeleteButton}>
          この建材を削除する
        </Button>
      )}
      <CustomSnackbar
        autoHideDuration={6000}
        open={!(Object.keys(errors).length === 0) || addErrorList !== null}
        text={textSnackbars()}
      />
    </Grid>
  );
};
export default AdminProductRegisterForm;
